import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { X, Share2, Copy, Check, Link, Clock, Users, Globe } from 'lucide-react'
import { playlistShares, type PlaylistShareResponse } from '../api/client'
import type { Playlist } from '../types'
import type { PlaylistVisibility } from '../types'

interface SharePlaylistModalProps {
  playlist: Playlist
  isOpen: boolean
  onClose: () => void
}

export default function SharePlaylistModal({ playlist, isOpen, onClose }: SharePlaylistModalProps) {
  const queryClient = useQueryClient()
  const [shareResult, setShareResult] = useState<PlaylistShareResponse | null>(null)
  const [copied, setCopied] = useState(false)
  const [expiresInHours, setExpiresInHours] = useState<number | ''>('')
  const [visibility, setVisibility] = useState<PlaylistVisibility>(playlist.visibility || 'private')

  const createShareMutation = useMutation({
    mutationFn: () => playlistShares.create(playlist.id, {
      expires_in_hours: expiresInHours || null,
    }),
    onSuccess: (data) => {
      setShareResult(data)
    },
  })

  const updateVisibilityMutation = useMutation({
    mutationFn: (newVisibility: PlaylistVisibility) => playlistShares.setVisibility(playlist.id, newVisibility),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['playlists'] })
      queryClient.invalidateQueries({ queryKey: ['playlists', playlist.id] })
    },
  })

  const handleCreateShare = () => {
    createShareMutation.mutate()
  }

  const handleVisibilityChange = (newVisibility: PlaylistVisibility) => {
    setVisibility(newVisibility)
    updateVisibilityMutation.mutate(newVisibility)
  }

  const getFullShareUrl = () => {
    if (!shareResult) return ''
    const apiOrigin = window.location.origin.replace('app.', 'api.')
    return `${apiOrigin}/share/playlist/${shareResult.token}`
  }

  const handleCopy = async () => {
    const url = getFullShareUrl()
    await navigator.clipboard.writeText(url)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  const handleClose = () => {
    setShareResult(null)
    setExpiresInHours('')
    createShareMutation.reset()
    onClose()
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={handleClose}>
      <div
        className="bg-dark-800 rounded-lg w-full max-w-md mx-4 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-dark-700">
          <div className="flex items-center gap-3">
            <Share2 size={20} className="text-primary-400" />
            <h2 className="text-lg font-semibold">Share Playlist</h2>
          </div>
          <button
            onClick={handleClose}
            className="p-1 rounded hover:bg-dark-700 text-dark-400 hover:text-dark-200"
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="p-4">
          {/* Playlist info */}
          <div className="mb-4 p-3 bg-dark-700/50 rounded-lg">
            <p className="font-medium truncate">{playlist.name}</p>
          </div>

          {/* Internal visibility toggle */}
          <div className="mb-6 p-4 bg-dark-700/30 rounded-lg border border-dark-600">
            <div className="flex items-center gap-3 mb-3">
              <Users size={18} className="text-primary-400" />
              <div>
                <p className="font-medium text-sm">Internal Visibility</p>
                <p className="text-xs text-dark-400">Make visible to all logged-in users</p>
              </div>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => handleVisibilityChange('private')}
                disabled={updateVisibilityMutation.isPending}
                className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-colors ${
                  visibility === 'private'
                    ? 'bg-dark-600 text-white'
                    : 'bg-dark-700/50 text-dark-400 hover:bg-dark-600/50'
                }`}
              >
                Private
              </button>
              <button
                onClick={() => handleVisibilityChange('internal')}
                disabled={updateVisibilityMutation.isPending}
                className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-colors ${
                  visibility === 'internal'
                    ? 'bg-primary-600 text-white'
                    : 'bg-dark-700/50 text-dark-400 hover:bg-dark-600/50'
                }`}
              >
                <Globe size={14} className="inline mr-1" />
                Visible to Users
              </button>
            </div>
            {updateVisibilityMutation.isSuccess && (
              <p className="text-xs text-green-400 mt-2">Visibility updated!</p>
            )}
          </div>

          {/* Public link section */}
          <div className="border-t border-dark-700 pt-4">
            <div className="flex items-center gap-3 mb-4">
              <Link size={18} className="text-primary-400" />
              <div>
                <p className="font-medium text-sm">Public Share Link</p>
                <p className="text-xs text-dark-400">Anyone with the link can view</p>
              </div>
            </div>

            {!shareResult ? (
              /* Share options */
              <div className="space-y-4">
                <div>
                  <label className="flex items-center gap-2 text-sm text-dark-300 mb-2">
                    <Clock size={14} />
                    Expires after (hours)
                  </label>
                  <input
                    type="number"
                    min="1"
                    placeholder="Never expires"
                    value={expiresInHours}
                    onChange={(e) => setExpiresInHours(e.target.value ? parseInt(e.target.value) : '')}
                    className="w-full px-3 py-2 bg-dark-700 border border-dark-600 rounded-lg text-sm focus:outline-none focus:border-primary-500"
                  />
                </div>

                {createShareMutation.isError && (
                  <p className="text-red-400 text-sm">
                    {createShareMutation.error instanceof Error
                      ? createShareMutation.error.message
                      : 'Failed to create share link'}
                  </p>
                )}

                <button
                  onClick={handleCreateShare}
                  disabled={createShareMutation.isPending}
                  className="w-full py-2 bg-primary-600 hover:bg-primary-700 disabled:bg-dark-600 rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
                >
                  {createShareMutation.isPending ? (
                    'Creating...'
                  ) : (
                    <>
                      <Link size={16} />
                      Create Share Link
                    </>
                  )}
                </button>
              </div>
            ) : (
              /* Share link result */
              <div className="space-y-4">
                <div className="p-3 bg-green-500/10 border border-green-500/30 rounded-lg">
                  <p className="text-green-400 text-sm font-medium mb-1">Share link created!</p>
                  <p className="text-dark-300 text-xs">
                    {shareResult.expires_at
                      ? `Expires: ${new Date(shareResult.expires_at).toLocaleString()}`
                      : 'Never expires'}
                  </p>
                </div>

                <div className="flex gap-2">
                  <input
                    type="text"
                    readOnly
                    value={getFullShareUrl()}
                    className="flex-1 px-3 py-2 bg-dark-700 border border-dark-600 rounded-lg text-sm focus:outline-none"
                  />
                  <button
                    onClick={handleCopy}
                    className="px-4 py-2 bg-primary-600 hover:bg-primary-700 rounded-lg font-medium transition-colors flex items-center gap-2"
                  >
                    {copied ? (
                      <>
                        <Check size={16} />
                        Copied!
                      </>
                    ) : (
                      <>
                        <Copy size={16} />
                        Copy
                      </>
                    )}
                  </button>
                </div>

                <button
                  onClick={() => setShareResult(null)}
                  className="w-full py-2 bg-dark-700 hover:bg-dark-600 rounded-lg text-sm transition-colors"
                >
                  Create another link
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
